import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, DollarSign, Edit2, Save, X } from 'lucide-react';

const AdminDelivery = () => {
  const navigate = useNavigate();
  const [deliveryMethods, setDeliveryMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [message, setMessage] = useState({ type: '', text: '' });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchDeliveryMethods();
  }, []);

  const fetchDeliveryMethods = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/delivery/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch delivery methods');
      }

      const data = await response.json();
      setDeliveryMethods(data);
    } catch (error) {
      console.error('Error fetching delivery methods:', error);
      setMessage({ type: 'error', text: 'Erreur lors du chargement des modes de livraison' });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (method) => {
    setEditingId(method.id);
    setEditForm({
      name: method.name,
      description: method.description,
      base_price_ttc: method.base_price_ttc || 0,
      price_per_kg_ttc: method.price_per_kg_ttc,
      is_active: method.is_active
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handleSaveEdit = async (methodId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/delivery/${methodId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(editForm)
      });

      if (response.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to update delivery method');
      }

      setMessage({ type: 'success', text: 'Mode de livraison mis à jour avec succès' });
      setEditingId(null);
      setEditForm({});
      fetchDeliveryMethods();
      
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error updating delivery method:', error);
      setMessage({ type: 'error', text: 'Erreur lors de la mise à jour' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-6xl mx-auto">
          <p className="text-center text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/admin')}
            className="text-gray-600 hover:text-gray-900 mb-4 flex items-center gap-2"
          >
            ← Retour au tableau de bord
          </button>
          <div className="flex items-center gap-3">
            <Package size={32} className="text-celadon" />
            <h1 className="text-3xl font-bold text-gray-900">Modes de livraison</h1>
          </div>
          <p className="text-gray-600 mt-2">Gérez les prix et paramètres des modes de livraison</p>
        </div>

        {/* Success/Error Message */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-lg ${message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {message.text}
          </div>
        )}

        {/* Delivery Methods Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {deliveryMethods.map((method) => (
            <div key={method.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              {editingId === method.id ? (
                // Edit Mode
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Nom</label>
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-celadon"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Description</label>
                    <textarea
                      value={editForm.description}
                      onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-celadon"
                      rows="3"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Prix de base TTC (€)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={editForm.base_price_ttc}
                      onChange={(e) => setEditForm({ ...editForm, base_price_ttc: parseFloat(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-celadon"
                    />
                    <p className="text-xs text-gray-500 mt-1">Montant fixe appliqué à chaque commande</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Prix TTC par kg (€/kg)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={editForm.price_per_kg_ttc}
                      onChange={(e) => setEditForm({ ...editForm, price_per_kg_ttc: parseFloat(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-celadon"
                    />
                    <p className="text-xs text-gray-500 mt-1">Montant multiplié par le poids total</p>
                  </div>

                  <div className="p-3 bg-blue-50 rounded text-sm text-blue-800">
                    <strong>Formule de calcul :</strong> Prix total = {editForm.base_price_ttc || 0}€ (base) + {editForm.price_per_kg_ttc || 0}€/kg × poids
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editForm.is_active}
                      onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                      className="w-4 h-4 text-celadon border-gray-300 rounded focus:ring-[#2ECC71]"
                    />
                    <label className="text-sm font-medium">Mode actif</label>
                  </div>

                  <div className="flex gap-2 pt-4">
                    <button
                      onClick={() => handleSaveEdit(method.id)}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-celadon text-white rounded hover:bg-celadon-light transition-colors"
                    >
                      <Save size={16} />
                      Enregistrer
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors"
                    >
                      <X size={16} />
                      Annuler
                    </button>
                  </div>
                </div>
              ) : (
                // View Mode
                <div>
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-1">{method.name}</h3>
                      <p className="text-sm text-gray-600">{method.description}</p>
                    </div>
                    <button
                      onClick={() => handleEdit(method)}
                      className="p-2 text-gray-600 hover:text-celadon hover:bg-gray-100 rounded transition-colors"
                    >
                      <Edit2 size={20} />
                    </button>
                  </div>

                  <div className="space-y-3 pt-4 border-t">
                    <div className="flex items-center justify-between bg-gray-50 p-2 rounded">
                      <span className="text-sm font-medium text-gray-600">Prix de base HT</span>
                      <span className="text-sm font-bold text-gray-900">{method.base_price_ht?.toFixed(2)} €</span>
                    </div>
                    <div className="flex items-center justify-between bg-gray-50 p-2 rounded">
                      <span className="text-sm font-medium text-gray-600">Prix de base TTC</span>
                      <span className="text-lg font-bold text-celadon">{method.base_price_ttc?.toFixed(2)} €</span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-600">Prix HT par kg</span>
                      <span className="text-lg font-bold text-gray-900">{method.price_per_kg_ht?.toFixed(2)} € / kg</span>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t">
                      <span className="text-sm font-bold text-gray-900">Prix TTC par kg</span>
                      <span className="text-xl font-bold text-celadon">{method.price_per_kg_ttc?.toFixed(2)} € / kg</span>
                    </div>
                    
                    <div className="mt-2 p-3 bg-blue-50 rounded text-xs text-blue-800">
                      <strong>📊 Formule :</strong> Prix de base + (Prix au kg × Poids)<br/>
                      <strong>Exemple pour 3 kg :</strong><br/>
                      {method.base_price_ttc?.toFixed(2)}€ + ({method.price_per_kg_ttc?.toFixed(2)}€ × 3 kg) = <strong>{(method.base_price_ttc + method.price_per_kg_ttc * 3).toFixed(2)}€ TTC</strong>
                    </div>
                    
                    <div className="pt-2">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${method.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {method.is_active ? '✓ Actif' : '✗ Inactif'}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Info Box */}
        <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="font-bold text-blue-900 mb-2">💡 Information sur la tarification</h3>
          <div className="text-sm text-blue-800 space-y-2">
            <p><strong>Formule de calcul unique pour tous les modes :</strong></p>
            <p className="font-mono bg-white p-2 rounded border border-blue-300">
              Prix total = Prix de base + (Prix au kg × Poids du colis)
            </p>
            <ul className="mt-2 space-y-1">
              <li>• Les prix HT sont calculés automatiquement (TVA 20%)</li>
              <li>• Vous pouvez régler le <strong>prix de base</strong> et le <strong>prix au kilo</strong> pour chaque mode</li>
              <li>• Seuls les modes actifs seront proposés aux clients</li>
              <li>• Les modifications sont appliquées immédiatement</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDelivery;
